import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { Mapping } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/mapping.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { EditIconSmall, useModalControl } from '@frontend/design-system';
import { useScheduleSettingsSmallMode } from '../../hooks/settings/use-schedule-settings-small-mode';
import { PageConfig } from '../../types';
import { AppointmentTypesBulkColorAssignModal } from '../appointment-types-bulk-color-assign-modal';
import { AppointmentTypesCompactTable } from './AppointmentTypesCompactTable';
import { AppointmentTypesLargeTable } from './AppointmentTypesLargeTable';

type AppointmentTypesTableProps = {
  locationId: string;
  pageConfig: PageConfig;
  mappingList: Mapping[];
  isLoading: boolean;
  isPaymentsEnabled?: boolean;
  debouncedSearchValue?: string;
  setPageConfig: Dispatch<SetStateAction<PageConfig>>;
  onEdit: (row: AppointmentType) => void;
  onArchive: (row: AppointmentType) => void;
  isMultiLocation: boolean;
};

export const AppointmentTypesTable = ({
  locationId,
  onEdit,
  onArchive,
  isMultiLocation,
  setPageConfig,
  debouncedSearchValue,
  pageConfig,
  ...props
}: AppointmentTypesTableProps) => {
  const { t } = useTranslation('scheduleAppointmentTypes');
  const { invalidateListAppointmentTypes, invalidateListMappings } =
    SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();
  const { isSmallMode } = useScheduleSettingsSmallMode();
  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM(locationId);
  const [selectedAppointmentTypes, setSelectedAppointmentTypes] = useState<AppointmentType[]>([]);

  const handleTableRefetch = useCallback(() => {
    invalidateListAppointmentTypes(locationId);
    invalidateListMappings(locationId);
  }, [locationId]);

  const colorAssignModalControl = useModalControl();

  const rowActions = useMemo(() => {
    return {
      actions: [
        {
          Icon: () => <Icon name='edit-small' />,
          label: t('Edit'),
          hide: (row: AppointmentType) => !row.details?.act,
          onClick: onEdit,
        },
        {
          Icon: () => <Icon name='archive-small' color='error' />,
          label: t('Archive'),
          css: { span: { color: theme.colors.critical50 } },
          hide: (row: AppointmentType) => !hasWSM || !row.details?.act,
          onClick: onArchive,
        },
        {
          Icon: () => <Icon name='unarchive-small' color='primary' />,
          label: t('Unarchive'),
          css: { span: { color: theme.colors.primary50 } },
          hide: (row: AppointmentType) => !hasWSM || row.details?.act,
          onClick: onArchive,
        },
      ],
    };
  }, [hasWSM, onArchive, onEdit, t]);

  const bulkActions = useMemo(
    () => [
      {
        Icon: EditIconSmall,
        label: t('Assign Colors'),
        onClick: (_: any, selectedRows: AppointmentType[]) => {
          if (setSelectedAppointmentTypes) {
            setSelectedAppointmentTypes(selectedRows);
          }
          colorAssignModalControl.openModal();
        },
      },
    ],
    [t, setSelectedAppointmentTypes, colorAssignModalControl]
  );

  return (
    <>
      <AppointmentTypesBulkColorAssignModal
        {...colorAssignModalControl.modalProps}
        isCalendarView={false}
        shouldShowLocationColumn={isMultiLocation}
        appointmentTypes={selectedAppointmentTypes}
        isEdit
      />
      {isSmallMode ? (
        <AppointmentTypesCompactTable
          {...props}
          locationId={locationId}
          handleTableRefetch={handleTableRefetch}
          rowActions={rowActions}
          bulkActions={bulkActions}
          setSelectedAppointmentTypes={setSelectedAppointmentTypes}
          isMultiLocation={isMultiLocation}
          pageConfig={pageConfig}
          debouncedSearchValue={debouncedSearchValue}
          setPageConfig={setPageConfig}
        />
      ) : (
        <AppointmentTypesLargeTable
          {...props}
          locationId={locationId}
          handleTableRefetch={handleTableRefetch}
          rowActions={rowActions}
          bulkActions={bulkActions}
          setSelectedAppointmentTypes={setSelectedAppointmentTypes}
          setPageConfig={setPageConfig}
          isMultiLocation={isMultiLocation}
          pageConfig={pageConfig}
          debouncedSearchValue={debouncedSearchValue}
        />
      )}
    </>
  );
};
